import { logSchemaFallback } from "_shared/_utilities/log-schema-failure";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import { z } from "zod";

/**
 * Which of this organization's templates its proposals to newly rolled-out firms are built from.
 *
 * A read model with one write, like `VendorRolloutBoardSchema` beside it: the API resolves the
 * record from the bearer token and there is no id to carry, so nothing here is constructed in the
 * browser as a domain object. It is a parsed shape and the request that replaces it.
 */

/** The gaps the API distinguishes. Mirrors `ClientProposalTemplateGap`. */
export const CLIENT_PROPOSAL_TEMPLATE_GAPS = ["NotConfigured", "Disabled", "NoFeeSchedule", "NoTeam"] as const;

/**
 * What a gap name this bundle does not recognize becomes.
 *
 * Only the name degrades. `gapReason` is prose the server wrote for a reader and is printed
 * whatever the name beside it says, which is the whole reason both travel: a client holding a
 * bundle that predates a fifth gap still reads the sentence telling them what to go and fix.
 */
export const UNRECOGNIZED_CLIENT_PROPOSAL_TEMPLATE_GAP = "Unrecognized";

export type ClientProposalTemplateGap =
  | (typeof CLIENT_PROPOSAL_TEMPLATE_GAPS)[number]
  | typeof UNRECOGNIZED_CLIENT_PROPOSAL_TEMPLATE_GAP;

const degradeUnrecognizedGap = ({ value }: { value: unknown }): ClientProposalTemplateGap => {
  logSchemaFallback({
    schemaName: "ClientProposalTemplateSchema",
    field: "gap",
    value,
    fallback: UNRECOGNIZED_CLIENT_PROPOSAL_TEMPLATE_GAP,
  });
  return UNRECOGNIZED_CLIENT_PROPOSAL_TEMPLATE_GAP;
};

const ClientProposalTemplateGapZ = z.enum([
  ...CLIENT_PROPOSAL_TEMPLATE_GAPS,
  UNRECOGNIZED_CLIENT_PROPOSAL_TEMPLATE_GAP,
]);

/**
 * The configuration as the API sends it.
 *
 * `discount` is a fraction between 0 and 1, which is what the API stores and refuses outside of —
 * the screen shows a percentage and converts at the edge (see `client-proposal-template-form.ts`).
 */
const ClientProposalTemplateJSONZ = z.object({
  clientLegalEntityId: z.uuid(),
  feeScheduleTemplateId: z.uuid().nullish(),
  teamTemplateId: z.uuid().nullish(),
  conflictsTemplateDocumentId: z.uuid().nullish(),
  policyTemplateDocumentIds: z.array(z.uuid()),
  discount: z.number(),
  negotiable: z.boolean(),
  conflictsCheckWaived: z.boolean(),
  autoCreateEnabled: z.boolean(),
  isReadyForAutoCreation: z.boolean(),
  gap: ClientProposalTemplateGapZ.catch(degradeUnrecognizedGap).nullish(),
  /** Why nothing is being built, in the words the roll-out records against the firm. */
  gapReason: z.string().nullish(),
});

export type ClientProposalTemplateJSON = z.infer<typeof ClientProposalTemplateJSONZ>;

/**
 * The whole record, replaced.
 *
 * A replace rather than a patch because the API takes one: every field is part of one offer, and
 * a partial update is how a client ends up with last quarter's discount against this quarter's
 * fee schedule.
 */
export type UpdateClientProposalTemplateRequest = {
  feeScheduleTemplateId: string | null;
  teamTemplateId: string | null;
  conflictsTemplateDocumentId: string | null;
  policyTemplateDocumentIds: string[];
  discount: number;
  negotiable: boolean;
  conflictsCheckWaived: boolean;
  autoCreateEnabled: boolean;
};

export default class ClientProposalTemplateSchema {
  public static readonly ClientProposalTemplateJSONZ = ClientProposalTemplateJSONZ;

  public static validateJSON(json: unknown): ClientProposalTemplateJSON {
    return parseOrThrow(ClientProposalTemplateJSONZ, json, {
      logError: true,
      schemaName: "ClientProposalTemplateSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValidJSON(json: unknown): json is ClientProposalTemplateJSON {
    return ClientProposalTemplateJSONZ.safeParse(json).success;
  }
}
